import { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Fade,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const ChatWizard = ({ pillarId, viewId }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [freeModels, setFreeModels] = useState([]);
  const [premiumModels, setPremiumModels] = useState([]);
  const [userTier, setUserTier] = useState('free');
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelMenuAnchor, setModelMenuAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Get the current primary color from the actual rendered theme
  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  
  useEffect(() => {
    // Read the actual color being used by checking a themed element
    const updateColor = () => {
      // Try multiple sources in order of preference
      const sources = [
        () => getComputedStyle(document.documentElement).getPropertyValue('--org-primary-color').trim(),
        () => getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim(),
        () => theme?.palette?.primary?.main,
        () => {
          // Try to find a MuiAppBar or other themed element and read its background color
          const appBar = document.querySelector('.MuiAppBar-root');
          if (appBar) {
            return getComputedStyle(appBar).backgroundColor;
          }
          return null;
        }
      ];
      
      for (const getColor of sources) {
        const color = getColor();
        if (color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)') {
          setPrimaryColor(color);
          return;
        }
      }
    };
    
    updateColor();
    
    // Use MutationObserver to detect theme/style changes instead of polling
    const observer = new MutationObserver(() => {
      updateColor();
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    return () => observer.disconnect();
  }, [theme]);

  useEffect(() => {
    checkIfEnabled();
  }, []);

  // Re-evaluate tier when auth user resolves (async after mount)
  useEffect(() => {
    if (user?.is_admin || user?.organization) {
      setUserTier('premium');
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && pillarId) {
      loadSuggestedQuestions();
    }
  }, [isOpen, pillarId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkIfEnabled = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/chat-wizard/settings`, { withCredentials: true });
      if (response.data.success) {
        const data = response.data.data;
        setIsEnabled(data.enabled);
        const free = data.freeModels || [];
        const premium = data.premiumModels || [];
        setFreeModels(free);
        setPremiumModels(premium);
        // Determine tier: prefer client-side auth check over backend response
        // (backend session may not be available in iframe context)
        const clientTier = (user?.is_admin || user?.organization) ? 'premium' : (data.userTier || 'free');
        setUserTier(clientTier);
        // Default to first free model (premium users can switch via picker)
        if (free.length > 0) setSelectedModel(free[0].id);
      }
    } catch (error) {
      console.error('Failed to check chat wizard settings:', error);
      setIsEnabled(false);
    }
  };

  const loadSuggestedQuestions = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/chat-wizard/context/${pillarId}`,
        { params: { viewId } }
      );
      if (response.data.success) {
        setSuggestedQuestions(response.data.data.suggestedQuestions || []);
      }
    } catch (error) {
      console.error('Failed to load suggested questions:', error);
    }
  };

  const scrollToBottom = () => {
    // Use scrollTop on the container instead of scrollIntoView to prevent
    // parent containers (like iframes on landing pages) from scrolling
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/chat-wizard/chat`, {
        pillarId,
        viewId,
        messages: messages.map(m => ({ role: m.role, content: m.content })),
        userMessage: textToSend,
        selectedModel: selectedModel || undefined
      }, { withCredentials: true });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.data.message.content,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Tooltip title="Chat with AI Assistant" placement="left">
          <IconButton
            onClick={() => setIsOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 60,
              height: 60,
              bgcolor: primaryColor,
              color: 'white',
              boxShadow: 3,
              fontFamily: 'var(--font-family-body, inherit)',
              '&:hover': {
                bgcolor: primaryColor,
                opacity: 0.85,
                boxShadow: 6
              },
              zIndex: 1000
            }}
          >
            <ChatIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      )}

      <Fade in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 400,
            height: 600,
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: primaryColor,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-family-body, inherit)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon />
              <Typography variant="h6">AI Assistant</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50],
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontFamily: 'var(--font-family-body, inherit)'
            }}
          >
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AutoAwesomeIcon sx={{ fontSize: 48, color: primaryColor, mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Hi! I'm your AI assistant. I can help you understand the data and trends for this pillar.
                </Typography>
                {suggestedQuestions.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Try asking:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      {suggestedQuestions.map((question, idx) => (
                        <Chip
                          key={idx}
                          label={question}
                          onClick={() => handleSuggestedQuestion(question)}
                          sx={{
                            cursor: 'pointer',
                            fontFamily: 'var(--font-family-body, inherit)',
                            '&:hover': {
                              bgcolor: primaryColor,
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {messages.map((message, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: '80%',
                    bgcolor: message.role === 'user' 
                      ? primaryColor 
                      : theme.palette.background.paper,
                    color: message.role === 'user' 
                      ? 'white' 
                      : theme.palette.text.primary,
                    borderRadius: 2,
                    fontFamily: 'var(--font-family-body, inherit)',
                    ...(message.isError && {
                      bgcolor: theme.palette.error.light,
                      color: theme.palette.error.contrastText
                    })
                  }}
                >
                  {message.role === 'assistant' ? (
                    <Box sx={{ 
                      '& p': { margin: '0.5em 0', fontSize: '0.875rem' },
                      '& p:first-of-type': { marginTop: 0 },
                      '& p:last-of-type': { marginBottom: 0 },
                      '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                      '& li': { margin: '0.25em 0' },
                      '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '0.75em 0 0.5em 0', fontSize: '1rem', fontWeight: 600 },
                      '& table': { 
                        borderCollapse: 'collapse', 
                        width: '100%', 
                        margin: '0.5em 0',
                        fontSize: '0.8rem'
                      },
                      '& th, & td': { 
                        border: '1px solid rgba(0,0,0,0.12)', 
                        padding: '0.5em',
                        textAlign: 'left',
                        verticalAlign: 'top'
                      },
                      '& th': { 
                        backgroundColor: 'rgba(0,0,0,0.05)', 
                        fontWeight: 600
                      },
                      '& code': { 
                        backgroundColor: 'rgba(0,0,0,0.05)', 
                        padding: '0.125em 0.25em', 
                        borderRadius: '3px',
                        fontSize: '0.85em'
                      },
                      '& pre': { 
                        backgroundColor: 'rgba(0,0,0,0.05)', 
                        padding: '0.75em', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        margin: '0.5em 0'
                      },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' },
                      fontFamily: 'var(--font-family-body, inherit)'
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-family-body, inherit)' }}>
                      {message.content}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      opacity: 0.7,
                      fontSize: '0.65rem'
                    }}
                  >
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Paper>
              </Box>
            ))}

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                  <CircularProgress size={20} sx={{ color: primaryColor }} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderTop: 1, borderColor: theme.palette.divider, fontFamily: 'var(--font-family-body, inherit)' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Ask a question..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  size="small"
                  sx={(freeModels.length > 0 || premiumModels.length > 0) ? { '& .MuiInputBase-root': { pb: '28px' } } : {}}
                />
                {(freeModels.length > 0 || premiumModels.length > 0) && (
                  <Tooltip title="Select AI model" placement="top">
                    <Box
                      onClick={(e) => setModelMenuAnchor(e.currentTarget)}
                      sx={{
                        position: 'absolute',
                        bottom: 7,
                        right: 10,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.4,
                        borderRadius: '14px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        fontSize: '0.72rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        fontFamily: 'var(--font-family-body, inherit)',
                        lineHeight: 1.4,
                        '&:hover': { borderColor: primaryColor, color: primaryColor }
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                      {[...freeModels, ...premiumModels].find(m => m.id === selectedModel)?.label || 'Select model'}
                      <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                sx={{ color: primaryColor, mb: 0.25 }}
              >
                <SendIcon />
              </IconButton>
            </Box>

            <Menu
              anchorEl={modelMenuAnchor}
              open={Boolean(modelMenuAnchor)}
              onClose={() => setModelMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 220, maxWidth: 300 } } }}
            >
              {freeModels.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontSize: '0.65rem', color: 'text.disabled', py: 0.5, minHeight: 'unset' }}>
                  DEMO
                </MenuItem>
              )}
              {freeModels.map((m) => (
                <MenuItem
                  key={m.id}
                  selected={selectedModel === m.id}
                  onClick={() => { setSelectedModel(m.id); setModelMenuAnchor(null); }}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <ListItemText primary={m.label || m.id} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                </MenuItem>
              ))}
              {premiumModels.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontSize: '0.65rem', color: 'text.disabled', py: 0.5, minHeight: 'unset', mt: 0.5 }}>
                  PREMIUM
                </MenuItem>
              )}
              {premiumModels.map((m) => {
                const canUse = userTier === 'premium';
                return (
                  <Tooltip key={m.id} title={canUse ? '' : 'Premium access required'} placement="right">
                    <span>
                      <MenuItem
                        selected={selectedModel === m.id}
                        disabled={!canUse}
                        onClick={() => { if (canUse) { setSelectedModel(m.id); setModelMenuAnchor(null); } }}
                        sx={{ fontSize: '0.8rem', opacity: canUse ? 1 : 0.5 }}
                      >
                        {!canUse && (
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </ListItemIcon>
                        )}
                        <ListItemText primary={m.label || m.id} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                      </MenuItem>
                    </span>
                  </Tooltip>
                );
              })}
            </Menu>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default ChatWizard;
